import { fabric } from 'fabric';

import { ANNOTATION_TYPE, DEFAULT_FONT } from '@/shared/ui';
import { pxToMv, mVToPx } from '@/shared/ui/components/FabricJS/Helper';

type TextAnnotationCtor = new (
  cv: fabric.Canvas,
  points: { x: number; y: number },
  offsetX: number,
  config: any,
  verticalChannelPlacement: number[],
  channelIndex: number,
) => any;

let TextAnnotationImpl: any;

(function () {
  TextAnnotationImpl = function (
    cv: fabric.Canvas,
    points: { x: number; y: number },
    offsetX: number,
    config: any,
    verticalChannelPlacement: number[],
    channelIndex: number,
  ) {
    this.type = ANNOTATION_TYPE.TEXT;
    this.canvas = cv;
    this.channelIndex = channelIndex;
    this.config = config;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.isVisible = true;
    this.x = points.x;
    this.y = points.y;
    this.isBeingSelected = false;
    this.spp = (config.samplingRate * config.wndDuration) / config.width;
    this.xr = offsetX + this.x * this.spp;
    this.yr = pxToMv(this.verticalChannelPlacement, this.config, this.y, this.channelIndex);
    this.stringValue = '';
  };

  TextAnnotationImpl.prototype.draw = function () {
    this.text = new fabric.IText(this.stringValue, {
      left: this.x,
      top: this.y,
      padding: 3,
      hasControls: false,
      fontSize: 14,
      width: 100,
      height: 50,
      hoverCursor: 'arrow',
      fill: 'red',
      borderColor: 'red',
      editingBorderColor: 'red',
      fontFamily: DEFAULT_FONT,
    });
    this.isDrawing = true;
    this.canvas.add(this.text);
    this.canvas.setActiveObject(this.text);
    this.text.enterEditing();
  };

  TextAnnotationImpl.prototype.drawImport = function () {
    this.text = new fabric.IText(this.stringValue, {
      left: this.x,
      top: this.y,
      padding: 3,
      hasControls: false,
      fontSize: 14,
      width: 100,
      height: 50,
      hoverCursor: 'arrow',
      fill: 'blue',
      borderColor: 'red',
      editingBorderColor: 'red',
      fontFamily: DEFAULT_FONT,
    });
    this.canvas.add(this.text);
  };

  TextAnnotationImpl.prototype.render = function () {
    this.canvas.renderAll();
  };

  TextAnnotationImpl.prototype.update = function (
    delta: { dx: number; dy: number },
    displayedChannelsArr: number[],
  ) {
    this.x += delta.dx;
    this.y += delta.dy;
    this.xr += delta.dx * this.spp;
    for (let i = 0; i < displayedChannelsArr.length; i += 1) {
      if (this.channelIndex === displayedChannelsArr[i]) {
        this.yr = pxToMv(this.verticalChannelPlacement, this.config, this.y, i);
      }
    }
    this.text.set({
      left: this.x,
      top: this.y,
    });
    this.text.setCoords();

    // *: Editing out-range
    // if (this.y + this.text.height >= 0 && this.y <= this.config.height && this.x + this.text.width >= 0 && this.x <= this.config.width) {
    //   this.isVisibility(true);
    // } else {
    //   this.isVisibility(false);
    // }
  };

  TextAnnotationImpl.prototype.updateConfig = function (
    config: any,
    offsetX: number,
    verticalChannelPlacement: number[],
    displayedChannelsArr: number[],
  ) {
    this.config = config;
    this.verticalChannelPlacement = verticalChannelPlacement;
    for (let i = 0; i < displayedChannelsArr.length; i += 1) {
      if (this.channelIndex === displayedChannelsArr[i]) {
        this.spp = (config.samplingRate * config.wndDuration) / config.width;
        this.x = (this.xr - offsetX) / this.spp;
        this.y = mVToPx(verticalChannelPlacement, config, this.yr, i);

        this.text.set({
          left: this.x,
          top: this.y,
          fontSize: 14,
        });
        this.text.setCoords();
        this.isVisibility(true);
        // *: Editing out-range
        // if (this.y + this.text.height >= 0 && this.y <= this.config.height) {
        //   this.isVisibility(true);
        // } else {
        //   this.isVisibility(false);
        // }
        break;
      } else {
        this.isVisibility(false);
      }
    }
  };

  TextAnnotationImpl.prototype.updateChannelIdx = function (channelIndex: number) {
    this.channelIndex = channelIndex;
  };

  TextAnnotationImpl.prototype.isEditing = function () {
    return this.text.isEditing;
  };

  TextAnnotationImpl.prototype.scroll = function (offsetX: number, displayedChannelsArr: number[]) {
    for (let i = 0; i < displayedChannelsArr.length; i += 1) {
      if (this.channelIndex === displayedChannelsArr[i]) {
        this.x = (this.xr - offsetX) / this.spp;
        this.text.set({ left: this.x });
        this.text.setCoords();

        if (
          (this.x < 0 || this.x > this.config.width) &&
          (this.x + this.text.width < 0 || this.x + this.text.width > this.config.width)
        ) {
          this.isVisibility(false);
        } else {
          this.isVisibility(true);
        }
        break;
      }
    }
  };

  TextAnnotationImpl.prototype.isSelected = function (pointer: { x: number; y: number }) {
    if (
      pointer.y > this.y - 3 &&
      pointer.y < this.y + this.text.height + 4 &&
      pointer.x > this.x - 3 &&
      pointer.x < this.x + this.text.width + 4
    ) {
      this.isBeingSelected = true;
      return true;
    }
    this.isBeingSelected = false;
    return false;
  };

  TextAnnotationImpl.prototype.select = function () {
    this.text.set({ fill: 'red', hasBorders: true });
  };

  TextAnnotationImpl.prototype.deselect = function () {
    this.text.set({ fill: 'blue', hasBorders: false });
    if (!this.text.text) {
      this.canvas.remove(this.text);
    }
    this.isDrawing = false;
  };

  TextAnnotationImpl.prototype.isVisibility = function (isVisibility: boolean) {
    this.isVisible = isVisibility;
    this.text.visible = isVisibility;
  };

  TextAnnotationImpl.prototype.getJSONObject = function () {
    // *: Convert the specific character to prevent error while parsing JSON to Object
    const str = this.text.text.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t');
    return {
      type: this.type,
      channelIndex: this.channelIndex,
      xr: this.xr,
      yr: this.yr,
      text: str,
    };
  };

  TextAnnotationImpl.prototype.import = function (data: any) {
    this.channelIndex = data.channelIndex;
    this.xr = data.xr;
    this.yr = data.yr;
    this.stringValue = data.text;

    this.x = this.xr / this.spp;
    this.y = mVToPx(this.verticalChannelPlacement, this.config, this.yr, this.channelIndex);
    // this.draw();
    this.drawImport();
  };
})();

const TextAnnotation: TextAnnotationCtor = TextAnnotationImpl;

export { TextAnnotation };
export default { TextAnnotation };
